import { getFormatByViews } from './_helpers/date-utils'
import { usePickerState } from './_shared/hooks/usePickerState'
import { useUtils } from './_shared/hooks/useUtils'
import { PureDateInput } from './_shared/PureDateInput'
import { datePickerDefaultProps } from './constants/prop-types'
import type { ParsableDate } from './constants/prop-types'
import { DatePickerToolbar } from './DatePickerToolbar'
import { makePickerWithState } from './Picker/makePickerWithState'
import type { WithKeyboardInputProps, WithPureInputProps } from './Picker/makePickerWithState'
import type { MaterialUiPickersDate } from './typings/date'
import type { OutterCalendarProps } from './views/Calendar/Calendar'

export type DatePickerView = 'year' | 'date' | 'month'

export interface BaseDatePickerProps extends OutterCalendarProps {
  /**
   * Min selectable date
   * @default Date(1900-01-01)
   */
  minDate?: ParsableDate
  /**
   * Max selectable date
   * @default Date(2100-01-01)
   */
  maxDate?: ParsableDate

  /**
   * Compare dates by the exact timestamp, instead of start/end of date
   * @default false
   */
  strictCompareDates?: boolean

  /**
   * Disable past dates
   * @default false
   */
  disablePast?: boolean
  /**
   * Disable future dates
   * @default false
   */
  disableFuture?: boolean
  /**
   * To animate scrolling to current year (using scrollIntoView)
   * @default false
   */
  animateYearScrolling?: boolean
  /** Callback firing on year change @DateIOType */
  onYearChange?: (date: MaterialUiPickersDate) => void
}

export interface DatePickerViewsProps extends BaseDatePickerProps {
  /**
   * Array of views to show
   * @type {Array<"year" | "date" | "month">}
   */
  views?: DatePickerView[]
  /** First view to show in DatePicker */
  openTo?: DatePickerView
}

export type DatePickerProps = WithPureInputProps & DatePickerViewsProps

export type KeyboardDatePickerProps = WithKeyboardInputProps & DatePickerViewsProps

const defaultProps = {
  ...datePickerDefaultProps,
  openTo: 'date' as DatePickerView,
  views: ['year', 'date'] as DatePickerView[],
}

function useOptions(props: DatePickerViewsProps) {
  const utils = useUtils()

  return {
    getDefaultFormat: () => getFormatByViews(props.views!, utils),
  }
}

export const DatePicker = makePickerWithState({
  useOptions,
  Input: PureDateInput,
  useState: usePickerState,
  DefaultToolbarComponent: DatePickerToolbar,
})

/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
// @ts-ignore
DatePicker.defaultProps = defaultProps
